import { CARRIER_COORDINATION as T } from "@/constants/testIds";
import {
  Ship, Anchor, Container, CalendarDays, Bell, CheckCircle2, Clock, FileCheck2,
} from "lucide-react";

const dashboardRows = [
  { label: "Carrier", value: "MSC", mono: false },
  { label: "Booking", value: "Confirmed", mono: false, tag: "green" },
  { label: "Container", value: "MSCU1234567", mono: true },
  { label: "POL", value: "Mersin", icon: Anchor },
  { label: "POD", value: "Mombasa", icon: Anchor },
  { label: "Vessel", value: "MSC Aurora", icon: Ship },
  { label: "ETA", value: "12 June", icon: CalendarDays },
  { label: "Shipment Status", value: "On Schedule", tag: "green" },
];

const notifications = [
  { icon: CheckCircle2, text: "Booking Confirmed", meta: "MSC · #B-58211", tone: "emerald" },
  { icon: Clock, text: "ETA Updated", meta: "12 Jun · MSC Aurora", tone: "blue" },
  { icon: Container, text: "Container Released", meta: "MSCU1234567", tone: "amber" },
  { icon: FileCheck2, text: "Documents Ready", meta: "B/L · Invoice · COO", tone: "slate" },
];

const toneMap = {
  emerald: "text-emerald-600 bg-emerald-50",
  blue: "text-blue-brand bg-blue-soft",
  amber: "text-amber-600 bg-amber-50",
  slate: "text-slate-600 bg-slate-50",
};

export default function WorkspaceSection() {
  return (
    <section id="workspace" data-testid={T.workspace} className="relative bg-[#fafbfd] py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-brand">The Workspace</p>
            <h2 className="mt-4 text-3xl font-semibold text-navy sm:text-4xl lg:text-[44px] lg:leading-[1.08]">
              The FreightIQ™ <br className="hidden sm:block" /> Workspace
            </h2>
            <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-slate-600">
              A single enterprise coordination surface for carrier booking management, container tracking and ocean freight coordination. Purpose-built for import operations teams — not repurposed inbox plugins.
            </p>

            <ul className="mt-8 space-y-3 text-[14px] text-slate-700">
              {[
                "Structured carrier profiles with contract references",
                "Booking timelines synced across your ops team",
                "Container-level visibility with document trails",
                "Notification center that replaces status-check emails",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-brand" strokeWidth={2.4} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative">
            <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-blue-soft blur-3xl" />
            <div className="relative rounded-2xl border border-slate-line bg-white shadow-card-lg">
              <div className="flex items-center gap-2 border-b border-slate-line px-5 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-red-300/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-300/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-300/70" />
                <span className="ml-3 font-mono text-[11px] text-slate-500">workspace / freightiq</span>
                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-blue-soft px-2 py-0.5 text-[10px] font-medium text-blue-brand">
                  <Bell className="h-3 w-3" />
                  Live
                </span>
              </div>

              <div className="grid gap-0 divide-y divide-slate-100 p-2 sm:p-4">
                <div className="grid grid-cols-2 gap-x-6 gap-y-4 px-3 py-4 sm:grid-cols-4">
                  {dashboardRows.map((row) => {
                    const Icon = row.icon;
                    return (
                      <div key={row.label} className="min-w-0">
                        <p className="text-[10px] uppercase tracking-widest text-slate-500">{row.label}</p>
                        <div className="mt-1 flex items-center gap-1.5">
                          {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" strokeWidth={2} />}
                          <p className={`truncate text-[13px] text-navy ${row.mono ? "font-mono" : "font-medium"}`}>
                            {row.value}
                          </p>
                          {row.tag === "green" && (
                            <span className="ml-1 h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="px-3 py-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] uppercase tracking-widest text-slate-500">Notification Center</p>
                    <span className="text-[11px] text-slate-400">Today</span>
                  </div>
                  <ul className="mt-3 space-y-2">
                    {notifications.map(({ icon: Icon, text, meta, tone }) => (
                      <li key={text} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white px-3 py-2.5 transition hover:border-slate-200">
                        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${toneMap[tone]}`}>
                          <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-medium text-navy">{text}</p>
                          <p className="truncate text-[11.5px] text-slate-500">{meta}</p>
                        </div>
                        <span className="rounded-full bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-500">new</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
